/**
 * Frozen PlayerCard geometry (docs/design/add-fullbody.md section 3.3). SVG viewBox 500x700.
 * Rects are {x, y, w, h}. Zones are [x, y, w, h].
 *
 * Horizontally the card is centered on x 242, the middle of CARD_BODY (4..480). The body stroke is
 * 8 wide and sits on the path, so the inner edge runs from x 8 to x 476. The art window outer edge
 * (stroke included) is 17..467, which leaves an equal 9 px gutter on both sides. The add-fullbody
 * numbers (window at 28..472, center 250) were lopsided, 17 px left and 1 px right; this file wins.
 */

export const VIEWBOX = [500, 700] as const;
export const CARD_BODY = { x: 4, y: 4, w: 476, h: 676, rx: 6, stroke: 8 } as const;
export const CARD_SHADOW = { dx: 16, dy: 16 } as const;
export const ART_WINDOW = { x: 20, y: 28, w: 444, h: 476, stroke: 6 } as const;
/** The figure may cross the art window border but never this clip. It sits inside the card's inner edge (8..476). */
export const POPOUT_CLIP = { x: 10, y: 12, w: 464, h: 500 } as const;
/**
 * Sticker figure top at y 40, soles at y 494 (target height 454), centered at x 242. `minTop` is the
 * clip top (12) plus the sticker's baked ink outline (about 7 px at this scale) plus 1 px of air,
 * so a clamped head keeps its whole outline. `maxW` is 420 so that the largest nudge scale (1.1) is
 * 462 wide and still inside the pop-out clip (464).
 */
export const FIGURE = { top: 40, soles: 494, maxW: 420, cx: 242, minTop: 20 } as const;
export const FEET_SHADOW = { cy: 496, ry: 10, rxFactor: 0.38, rxMin: 60, rxMax: 150 } as const;
/**
 * `midShare`: the middle of the digits sits this share of the font size above the baseline (cap
 * height 0.69 / 2). The skew pivots there, not on the baseline, so it does not push the numeral sideways.
 */
export const GIANT_NUMBER = { x: 242, baseline: 360, size: 300, skewDeg: -7, midShare: 0.345 } as const;
/**
 * The tier zone is 134 wide (x 322..456): "BENCHWARMER" and "COMBINE PENDING" need that width to stay
 * readable. Its right edge (456) still mirrors the OVR zone's left edge (28) around the window.
 */
export const ZONES = {
  ovr: [28, 36, 114, 114],
  tier: [322, 36, 134, 74],
  nameplate: [20, 500, 444, 66],
  attrs: [28, 578, 428, 86],
} as const;
export const EXPORT = { w: 1080, h: 1512 } as const;

export const CARD_GEOMETRY = {
  VIEWBOX,
  CARD_BODY,
  CARD_SHADOW,
  ART_WINDOW,
  POPOUT_CLIP,
  FIGURE,
  FEET_SHADOW,
  GIANT_NUMBER,
  ZONES,
  EXPORT,
} as const;

export interface FigureLayout {
  /** Figure box inside the sticker PNG [x, y, w, h] (PortraitLayout.stickerBox). */
  stickerBox: readonly [number, number, number, number];
  stickerW: number;
  stickerH: number;
}

export interface FigurePlacement {
  /** Where to draw the whole sticker image, in card coordinates. */
  image: { x: number; y: number; w: number; h: number };
  /** The figure's own box in card coordinates (what must stay inside POPOUT_CLIP). */
  box: { x: number; y: number; w: number; h: number };
  scale: number;
}

/**
 * The one placement rule for the figure, shared by the SVG card, the PNG export and the studio
 * compositor so they always agree. Soles land on FIGURE.soles (plus the nudge), the box is
 * centered on FIGURE.cx, the height is the target 454 unless that makes it wider than FIGURE.maxW,
 * and the head never goes above FIGURE.minTop (the soles stay fixed and the figure shrinks).
 * `nudge` is the portrait's {dy -20..20, scale 0.9..1.1}.
 */
export function fitFigure(layout: FigureLayout, nudge?: { dy: number; scale: number } | null): FigurePlacement {
  const [bx, by, bw, bh] = layout.stickerBox;
  let s = (FIGURE.soles - FIGURE.top) / bh;
  if (bw * s > FIGURE.maxW) s = FIGURE.maxW / bw;
  s *= nudge?.scale ?? 1;
  const soles = FIGURE.soles + (nudge?.dy ?? 0);
  if (soles - bh * s < FIGURE.minTop) s = (soles - FIGURE.minTop) / bh;
  const x = FIGURE.cx - (bx + bw / 2) * s;
  const y = soles - (by + bh) * s;
  return {
    image: { x, y, w: layout.stickerW * s, h: layout.stickerH * s },
    box: { x: x + bx * s, y: y + by * s, w: bw * s, h: bh * s },
    scale: s,
  };
}
